import { useCallback, useEffect, useState } from "react";
import logoUrl from "@spillcraft/brand/assets/logos/svg/spillcraft-logo.svg";

interface Dataset {
  count: number;
  lastRun: string | null;
  lastError: string | null;
  lastResult: { fetched: number; stored: number; pruned: number } | null;
}
interface Status {
  ok: boolean;
  deals: Dataset;
  activities: Dataset;
}
interface DataResp {
  columns: string[];
  rows: Array<Record<string, unknown>>;
}
interface LabelGroup {
  field: string;
  title: string;
  hint: string;
  values: string[];
  labels: Record<string, string>;
}
interface Settings {
  windowMonths: string;
  pipelineIds: string;
  customerTypeFieldId: string;
  prospectValue: string;
  customerValue: string;
  activityTypes: string;
  pipedriveApiToken: string;
  exportToken: string;
}

const SETTING_FIELDS: Array<{ key: keyof Settings; label: string; hint: string }> = [
  { key: "windowMonths", label: "Export window (months)", hint: "How far back each run reaches. Empty or 0 = all history." },
  { key: "pipelineIds", label: "Pipeline IDs", hint: "Comma-separated. Deals outside these are skipped. Empty = all." },
  { key: "activityTypes", label: "Activity types", hint: "Comma-separated Pipedrive type keys to include." },
  { key: "customerTypeFieldId", label: "Customer Type field id", hint: "Org custom field hash used to split prospect vs customer." },
  { key: "prospectValue", label: "Prospect value", hint: "Option id that marks an organisation as a prospect." },
  { key: "customerValue", label: "Customer value", hint: "Option id that marks an organisation as a customer." },
];

interface PdTest {
  ok: boolean;
  source?: "entered" | "saved" | "secret";
  user?: string;
  email?: string;
  company?: string;
  domain?: string;
  error?: string;
}

type View = "overview" | "data" | "labels" | "export" | "connection" | "urls";

const NAV: Array<{ id: View; label: string; desc: string }> = [
  { id: "overview", label: "Overview", desc: "Run status, row counts, refresh now" },
  { id: "data", label: "Data", desc: "Browse the exact rows Phocas receives" },
  { id: "labels", label: "Rename values", desc: "Relabel types, stages and statuses" },
  { id: "export", label: "Export options", desc: "Window, pipelines and field ids" },
  { id: "connection", label: "Connection", desc: "Pipedrive and export tokens" },
  { id: "urls", label: "Pull URLs", desc: "CSV endpoints for Phocas" },
];

// Line-icon glyphs for the section tiles (ported from the design).
const GLYPHS: Record<
  View,
  { rects?: number[][]; lines?: number[][]; dots?: number[][] }
> = {
  overview: { rects: [[4, 13, 4, 7], [10, 8, 4, 12], [16, 4, 4, 16]] },
  data: { rects: [[3.5, 4.5, 17, 15]], lines: [[3.5, 9.5, 20.5, 9.5], [3.5, 14.5, 20.5, 14.5], [9, 4.5, 9, 19.5]] },
  labels: { rects: [[3.5, 7, 7, 10], [13.5, 7, 7, 10]], lines: [[10.5, 12, 13.5, 12]] },
  export: { lines: [[4, 8, 20, 8], [4, 16, 20, 16]], dots: [[9, 8, 2.4], [15, 16, 2.4]] },
  connection: { lines: [[9, 12, 15, 12]], dots: [[6, 12, 3], [18, 12, 3]] },
  urls: { lines: [[12, 4, 12, 14], [8, 10.5, 12, 14.5], [16, 10.5, 12, 14.5], [4.5, 19, 19.5, 19]] },
};

const GRADIENT = { background: "var(--sc-gradient-brand)" };
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString("en-GB") : "never");
const latest = (a: string | null, b: string | null) => [a, b].filter(Boolean).sort().pop() ?? null;

function Glyph({ view }: { view: View }) {
  const g = GLYPHS[view];
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="var(--sc-accent)" strokeWidth={1.6} strokeLinecap="round" aria-hidden="true">
      {(g.rects ?? []).map((r, i) => (
        <rect key={`r${i}`} x={r[0]} y={r[1]} width={r[2]} height={r[3]} rx={1.5} />
      ))}
      {(g.lines ?? []).map((l, i) => (
        <line key={`l${i}`} x1={l[0]} y1={l[1]} x2={l[2]} y2={l[3]} />
      ))}
      {(g.dots ?? []).map((d, i) => (
        <circle key={`d${i}`} cx={d[0]} cy={d[1]} r={d[2]} />
      ))}
    </svg>
  );
}

const H1 = ({ children }: { children: React.ReactNode }) => (
  <h1 className="text-[22px] font-semibold tracking-[-0.015em] text-headline">{children}</h1>
);
const Lead = ({ children }: { children: React.ReactNode }) => (
  <p className="mt-1 text-[13.5px] text-muted">{children}</p>
);

export default function App() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [status, setStatus] = useState<Status | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [labelGroups, setLabelGroups] = useState<LabelGroup[] | null>(null);
  const [toast, setToast] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<View>("overview");
  const [dataTab, setDataTab] = useState<"deals" | "activities" | null>(null);
  const [data, setData] = useState<DataResp | null>(null);
  const [filter, setFilter] = useState("");
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());
  const [reveal, setReveal] = useState({ pd: false, ex: false });
  const [copied, setCopied] = useState<string | null>(null);
  const [pdTest, setPdTest] = useState<PdTest | "testing" | null>(null);
  const [pdTypes, setPdTypes] = useState<Array<{ key: string; name: string }> | null>(null);

  const origin = window.location.origin;

  const loadStatus = useCallback(async () => {
    const r = await fetch("/api/status");
    if (r.ok) setStatus((await r.json()) as Status);
  }, []);
  const loadSettings = useCallback(async () => {
    const r = await fetch("/api/settings");
    if (r.ok) setSettings((await r.json()) as Settings);
  }, []);
  const loadLabels = useCallback(async () => {
    const r = await fetch("/api/labels");
    if (r.ok) setLabelGroups(((await r.json()) as { groups: LabelGroup[] }).groups);
  }, []);
  const loadData = useCallback(async (which: "deals" | "activities") => {
    setDataTab(which);
    setData(null);
    setHiddenTypes(new Set());
    const r = await fetch(`/api/data/${which}`);
    if (r.ok) setData((await r.json()) as DataResp);
  }, []);

  useEffect(() => {
    fetch("/api/auth/status")
      .then((r) => r.json() as Promise<{ authenticated: boolean }>)
      .then((d) => setAuthed(d.authenticated))
      .catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (authed) {
      void loadStatus();
      void loadSettings();
      void loadLabels();
    }
  }, [authed, loadStatus, loadSettings, loadLabels]);

  useEffect(() => {
    if (authed && view === "data" && !dataTab) void loadData("deals");
  }, [authed, view, dataTab, loadData]);

  useEffect(() => {
    if (authed && (view === "export" || view === "labels") && pdTypes === null) {
      fetch("/api/pipedrive/activity-types")
        .then((r) => (r.ok ? (r.json() as Promise<{ types: Array<{ key: string; name: string }> }>) : null))
        .then((d) => setPdTypes(d?.types ?? []))
        .catch(() => setPdTypes([]));
    }
  }, [authed, view, pdTypes]);

  const pdName = (key: string) => pdTypes?.find((t) => t.key === key)?.name;

  // Activity types to manage = Pipedrive's list ∪ types present in the data
  // (so a type only in the data — e.g. a disabled/renamed one like
  // "follow_up_call" — still shows up to include/rename).
  const dataActivityTypes = () =>
    labelGroups?.find((g) => g.field === "activity_type")?.values ?? [];
  const allActivityKeys = () =>
    Array.from(new Set([...(pdTypes ?? []).map((t) => t.key), ...dataActivityTypes()]));
  const activityOptions = (): Array<{ key: string; name: string }> => {
    const fromPd = pdTypes ?? [];
    const seen = new Set(fromPd.map((t) => t.key));
    return [...fromPd, ...dataActivityTypes().filter((k) => !seen.has(k)).map((k) => ({ key: k, name: k }))];
  };

  // Toggle one activity type in the settings.activityTypes comma list.
  // Empty list = all types; a full selection collapses back to empty.
  const toggleActivityType = (key: string) => {
    if (!settings) return;
    const all = allActivityKeys();
    const cur = new Set(settings.activityTypes.split(",").map((s) => s.trim()).filter(Boolean));
    const eff = cur.size ? new Set(cur) : new Set(all);
    if (eff.has(key)) eff.delete(key);
    else eff.add(key);
    const list = all.filter((k) => eff.has(k));
    setSettings({ ...settings, activityTypes: list.length === all.length ? "" : list.join(",") });
  };

  const login = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    const r = await fetch("/api/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    if (r.ok) {
      setPassword("");
      setAuthed(true);
    } else {
      setLoginError("Invalid password");
    }
  };

  const logout = async () => {
    await fetch("/api/logout", { method: "POST" });
    setAuthed(false);
    setStatus(null);
    setSettings(null);
    setLabelGroups(null);
    setData(null);
    setDataTab(null);
    setPdTypes(null);
    setView("overview");
  };

  const refresh = async () => {
    setBusy(true);
    setToast("Refresh started…");
    await fetch("/api/admin/refresh", { method: "POST" });
    for (let i = 0; i < 6; i++) {
      await new Promise((res) => setTimeout(res, 2000));
      await loadStatus();
    }
    setBusy(false);
    setToast("Refresh complete.");
  };

  const saveSettings = async () => {
    if (!settings) return;
    setBusy(true);
    const r = await fetch("/api/settings", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings),
    });
    setBusy(false);
    setToast(r.ok ? "Saved." : "Save failed.");
  };

  // Checks the token currently in the field (saved or not); blank falls back
  // server-side to the saved setting / Worker secret.
  const testPipedrive = async () => {
    setPdTest("testing");
    try {
      const r = await fetch("/api/pipedrive/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: settings?.pipedriveApiToken ?? "" }),
      });
      setPdTest((await r.json()) as PdTest);
    } catch {
      setPdTest({ ok: false, error: "Could not reach the portal API" });
    }
  };

  const saveLabels = async () => {
    if (!labelGroups) return;
    setBusy(true);
    const groups = Object.fromEntries(labelGroups.map((g) => [g.field, g.labels]));
    const r = await fetch("/api/labels", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ groups }),
    });
    setBusy(false);
    setToast(r.ok ? "Labels saved." : "Save failed.");
    if (r.ok && dataTab) void loadData(dataTab);
  };

  // Activity-type labels live in the activity_type relabel group.
  const activityLabel = (key: string) =>
    labelGroups?.find((g) => g.field === "activity_type")?.labels[key] ?? "";
  const setActivityLabel = (key: string, val: string) =>
    setLabelGroups((prev) =>
      prev
        ? prev.map((g) => (g.field === "activity_type" ? { ...g, labels: { ...g.labels, [key]: val } } : g))
        : prev
    );

  // Export options: save both the settings (which types to push) and the
  // labels (what each is called) in one action.
  const saveExportOptions = async () => {
    if (!settings) return;
    setBusy(true);
    const s = await fetch("/api/settings", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings),
    });
    let labelsOk = true;
    if (labelGroups) {
      const groups = Object.fromEntries(labelGroups.map((g) => [g.field, g.labels]));
      const l = await fetch("/api/labels", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ groups }),
      });
      labelsOk = l.ok;
    }
    setBusy(false);
    setToast(s.ok && labelsOk ? "Saved." : "Save failed.");
    if (s.ok && dataTab) void loadData(dataTab);
  };

  const copyUrl = (key: string, url: string) => {
    if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied((c) => (c === key ? null : c)), 1600);
  };

  if (authed === null) {
    return <div className="flex min-h-screen items-center justify-center bg-mist text-muted">Loading…</div>;
  }

  if (!authed) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-mist px-6 font-sans text-dark">
        <div className="absolute inset-x-0 top-1/2 h-60 -translate-y-1/2 bg-secondary" aria-hidden="true" />
        <div className="relative z-10 w-full max-w-sm overflow-hidden rounded-lg border border-mist bg-paper shadow-lg">
          <div className="h-1.5 w-full" style={GRADIENT} />
          <div className="p-8">
            <img src={logoUrl} alt="Spillcraft" className="mx-auto h-16 w-16" />
            <h1 className="mt-4 text-center text-lg font-semibold text-secondary">Phocas Export</h1>
            <p className="mt-0.5 text-center text-xs text-muted">Spillcraft · Spill Control Solutions</p>
            <form onSubmit={login} className="mt-6 space-y-3">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Portal password"
                className="w-full rounded border border-mist bg-paper px-3 py-2 text-dark outline-none focus:border-primary"
                autoFocus
              />
              <button type="submit" className="w-full rounded bg-primary px-3 py-2 font-medium text-white hover:bg-primary-dark">
                Sign in
              </button>
              {loginError && <p className="text-sm text-danger">{loginError}</p>}
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Derived connection health from the last run.
  const lastRun = status ? latest(status.deals.lastRun, status.activities.lastRun) : null;
  const hasError = !!(status && (status.deals.lastError || status.activities.lastError));
  const health = !status || !lastRun ? "idle" : hasError ? "error" : "ok";
  const healthDot = health === "ok" ? "var(--sc-success)" : health === "error" ? "var(--sc-danger)" : "var(--sc-slate)";
  const healthText = health === "ok" ? "Healthy" : health === "error" ? "Needs attention" : "Not run yet";
  const errorText = status?.deals.lastError || status?.activities.lastError || "";

  const activityTypeValues =
    dataTab === "activities" && data
      ? Array.from(new Set(data.rows.map((r) => String(r.type ?? "")).filter(Boolean))).sort()
      : [];
  const filteredRows = (data?.rows ?? []).filter((row) => {
    if (dataTab === "activities" && hiddenTypes.has(String(row.type ?? ""))) return false;
    if (filter && !data!.columns.some((c) => String(row[c] ?? "").toLowerCase().includes(filter.toLowerCase())))
      return false;
    return true;
  });

  return (
    <div className="flex min-h-screen flex-col bg-mist font-sans text-dark">
      <div className="h-[3px] w-full" style={GRADIENT} />

      <header className="bg-secondary">
        <div className="mx-auto flex max-w-[1180px] items-center justify-between gap-6 px-7 py-3.5">
          <button onClick={() => setView("overview")} className="flex items-center gap-3.5 text-left" title="Overview">
            <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-paper p-[3px]">
              <img src={logoUrl} alt="Spillcraft" className="h-full w-full" />
            </span>
            <span>
              <span className="block text-base font-semibold tracking-[-0.01em] text-white">Phocas Export</span>
              <span className="block text-xs text-white/60">Pipedrive → CSV feeds for Phocas</span>
            </span>
          </button>
          <div className="flex items-center gap-5">
            <div className="hidden items-center gap-2 text-xs text-white/70 sm:flex">
              <span className="h-[7px] w-[7px] rounded-full" style={{ background: healthDot }} />
              <span>{health === "ok" ? "Pipedrive connected" : health === "error" ? "Attention needed" : "Awaiting first run"}</span>
            </div>
            <button onClick={logout} className="text-[13px] text-white/70 hover:text-white">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid w-full max-w-[1180px] flex-1 grid-cols-1 items-start gap-6 px-7 pb-10 pt-6 md:grid-cols-[216px_1fr]">
        <nav className="flex flex-col gap-0.5 rounded-lg border border-mist bg-paper p-2 md:sticky md:top-6">
          {NAV.map((n) => {
            const active = view === n.id;
            return (
              <button
                key={n.id}
                onClick={() => setView(n.id)}
                className={`flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13.5px] ${
                  active ? "bg-mist font-semibold text-headline" : "font-normal text-muted hover:bg-mist"
                }`}
              >
                <span
                  className="h-1.5 w-1.5 flex-none rounded-sm"
                  style={{ background: active ? "var(--sc-accent)" : "var(--sc-slate)", opacity: active ? 1 : 0.35 }}
                />
                <span>{n.label}</span>
              </button>
            );
          })}
          <div className="mx-1 mt-2 border-t border-mist pt-2.5 text-[11px] leading-normal text-muted">
            Last full run
            <br />
            <span className="text-dark">{fmt(lastRun)}</span>
          </div>
        </nav>

        <main className="flex min-w-0 flex-col gap-5">
          {view === "overview" && (
            <>
              <div>
                <H1>Overview</H1>
                <Lead>Feeds refresh every 4 hours; Phocas pulls the CSVs on its own schedule.</Lead>
              </div>
              {status && (
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-lg border border-mist bg-paper p-4">
                      <div className="text-[11px] uppercase tracking-[0.07em] text-muted">Last refresh</div>
                      <div className="mt-2 text-lg font-semibold text-headline">{fmt(lastRun)}</div>
                      <div className="mt-0.5 text-[12.5px] text-muted">
                        {status.deals.lastResult
                          ? `${status.deals.lastResult.stored + (status.activities.lastResult?.stored ?? 0)} rows written`
                          : "no runs yet"}
                      </div>
                    </div>
                    <div className="rounded-lg border border-mist bg-paper p-4">
                      <div className="text-[11px] uppercase tracking-[0.07em] text-muted">Connection</div>
                      <div className="mt-2 flex items-center gap-2 text-lg font-semibold text-headline">
                        <span className="h-2 w-2 rounded-full" style={{ background: healthDot }} />
                        <span>{healthText}</span>
                      </div>
                      <div className="mt-0.5 text-[12.5px] text-muted">
                        {health === "error" ? "A feed errored on its last run" : "Pipedrive token in use"}
                      </div>
                    </div>
                    <div className="rounded-lg border border-mist bg-paper p-4">
                      <div className="text-[11px] uppercase tracking-[0.07em] text-muted">Rows exported</div>
                      <div className="mt-2 text-lg font-semibold text-headline">
                        {status.deals.count + status.activities.count}
                      </div>
                      <div className="mt-0.5 text-[12.5px] text-muted">
                        {status.deals.count} deals · {status.activities.count} activities
                      </div>
                    </div>
                  </div>

                  {hasError && (
                    <div
                      className="flex items-center justify-between gap-5 rounded-lg border border-mist bg-paper p-3.5"
                      style={{ borderLeft: "3px solid var(--sc-accent)" }}
                    >
                      <div>
                        <div className="text-[13.5px] font-semibold text-headline">A feed reported an error on its last run</div>
                        <div className="mt-0.5 text-[12.5px] text-muted">{errorText}</div>
                      </div>
                      <button
                        onClick={() => setView("connection")}
                        className="flex-none rounded-md bg-primary px-4 py-2 text-[13px] font-semibold text-white hover:bg-primary-dark"
                      >
                        Check connection
                      </button>
                    </div>
                  )}

                  <div>
                    <div className="mb-2.5 flex items-baseline justify-between">
                      <h2 className="text-[13px] font-semibold uppercase tracking-[0.07em] text-muted">Sections</h2>
                      <div className="flex items-center gap-3">
                        {toast && <span className="text-[12.5px] text-muted">{toast}</span>}
                        <button
                          onClick={refresh}
                          disabled={busy}
                          className="rounded-md border border-secondary px-3.5 py-2 text-[13px] font-semibold text-secondary hover:bg-secondary hover:text-white disabled:opacity-50"
                        >
                          {busy ? "Working…" : "Run all feeds now"}
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      {NAV.filter((n) => n.id !== "overview").map((n) => (
                        <button
                          key={n.id}
                          onClick={() => setView(n.id)}
                          className="flex min-h-[118px] flex-col gap-1.5 rounded-lg border border-mist bg-paper p-4 text-left hover:border-primary hover:shadow-md"
                        >
                          <Glyph view={n.id} />
                          <div className="mt-0.5 text-[14.5px] font-semibold text-headline">{n.label}</div>
                          <div className="text-[12.5px] leading-snug text-muted">{n.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {view === "data" && (
            <>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <H1>Data</H1>
                  <Lead>Exactly the rows Phocas receives, with your labels applied. Read-only.</Lead>
                </div>
                <input
                  placeholder="Filter rows"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="w-52 rounded-md border border-mist bg-paper px-3 py-2 text-[13px] text-dark outline-none focus:border-primary"
                />
              </div>
              <div className="flex gap-1 border-b border-mist">
                {(["deals", "activities"] as const).map((w) => (
                  <button
                    key={w}
                    onClick={() => loadData(w)}
                    className={`-mb-px border-b-2 px-3 py-2.5 text-[13px] ${
                      dataTab === w
                        ? "border-primary font-semibold text-headline"
                        : "border-transparent font-normal text-muted hover:text-headline"
                    }`}
                  >
                    {w === "deals" ? "Deals" : "Activities"}
                  </button>
                ))}
              </div>
              {activityTypeValues.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[12px] text-muted">Show types:</span>
                  {activityTypeValues.map((tp) => {
                    const shown = !hiddenTypes.has(tp);
                    return (
                      <button
                        key={tp}
                        onClick={() =>
                          setHiddenTypes((prev) => {
                            const next = new Set(prev);
                            if (next.has(tp)) next.delete(tp);
                            else next.add(tp);
                            return next;
                          })
                        }
                        className={`rounded-full border px-2.5 py-1 text-[12px] ${
                          shown ? "border-secondary bg-secondary text-white" : "border-mist text-muted line-through"
                        }`}
                      >
                        {tp}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="overflow-hidden rounded-lg border border-mist bg-paper">
                {!data && <p className="p-4 text-sm text-muted">Loading…</p>}
                {data && (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-[13px]">
                        <thead>
                          <tr className="bg-mist/60">
                            {data.columns.map((c) => (
                              <th
                                key={c}
                                className="whitespace-nowrap border-b border-mist px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-muted"
                              >
                                {c}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRows.map((row, i) => (
                            <tr key={i} className="border-b border-mist/70">
                              {data.columns.map((c) => (
                                <td key={c} className="whitespace-nowrap px-4 py-2.5 text-secondary">
                                  {row[c] === null || row[c] === undefined ? "" : String(row[c])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-mist/40 px-4 py-2.5 text-[12.5px] text-muted">
                      Showing {filteredRows.length}
                      {filter ? ` of ${data.rows.length}` : ""} rows
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {view === "labels" && labelGroups && (
            <>
              <div>
                <H1>Rename values</H1>
                <Lead>Deal stage and status labels are what Phocas reports on; the left comes from Pipedrive. Activity types are renamed in Export options. Non-destructive.</Lead>
              </div>
              <div className="overflow-hidden rounded-lg border border-mist bg-paper">
                {labelGroups.filter((g) => g.field !== "activity_type").map((g) => (
                  <div key={g.field}>
                    <div className="flex justify-between border-b border-mist bg-mist/60 px-[18px] py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
                      <span>{g.title}</span>
                      <span className="font-normal normal-case tracking-normal">{g.values.length} values</span>
                    </div>
                    {g.values.map((v) => (
                      <div key={v} className="grid grid-cols-[1fr_24px_1fr] items-center gap-3 border-b border-mist/70 px-[18px] py-2.5">
                        <span className="truncate text-[13px]" title={v}>
                          <span className="font-mono text-muted">{v}</span>
                          {g.field === "activity_type" && pdName(v) && (
                            <span className="ml-2 text-secondary">{pdName(v)}</span>
                          )}
                        </span>
                        <span className="text-center text-[13px] text-muted">→</span>
                        <input
                          value={g.labels[v] ?? ""}
                          placeholder={g.field === "activity_type" ? pdName(v) ?? v : v}
                          onChange={(e) =>
                            setLabelGroups(
                              labelGroups.map((x) =>
                                x.field === g.field ? { ...x, labels: { ...x.labels, [v]: e.target.value } } : x
                              )
                            )
                          }
                          className="w-full rounded-md border border-mist bg-paper px-2.5 py-1.5 text-[13px] text-dark outline-none focus:border-primary"
                        />
                      </div>
                    ))}
                    {g.values.length === 0 && <p className="px-[18px] py-2.5 text-[13px] text-muted">Nothing yet — run a refresh first.</p>}
                  </div>
                ))}
                <div className="flex items-center gap-3 bg-mist/40 px-[18px] py-3.5">
                  <button
                    onClick={saveLabels}
                    disabled={busy}
                    className="rounded-md bg-primary px-[18px] py-2 text-[13px] font-semibold text-white hover:bg-primary-dark disabled:opacity-50"
                  >
                    Save labels
                  </button>
                  <span className="text-[12.5px] text-muted">
                    {toast || "Applies on the next run and to live pulls. Unmapped values pass through."}
                  </span>
                </div>
              </div>
            </>
          )}

          {view === "export" && settings && (
            <>
              <div>
                <H1>Export options</H1>
                <Lead>Scope of every run.</Lead>
              </div>
              <div className="flex flex-col gap-5 rounded-lg border border-mist bg-paper p-5">
                {SETTING_FIELDS.map((f, i) => {
                  const typeSet = new Set(
                    settings.activityTypes.split(",").map((s) => s.trim()).filter(Boolean)
                  );
                  const isTypes = f.key === "activityTypes";
                  const options = isTypes ? activityOptions() : [];
                  return (
                    <div key={f.key}>
                      <div className="grid items-start gap-4 sm:grid-cols-[220px_1fr]">
                        <div>
                          <div className="text-[13.5px] font-semibold text-headline">{f.label}</div>
                          <div className="mt-0.5 text-[12.5px] text-muted">
                            {isTypes ? "Tick which types to push to Phocas, and rename how each appears." : f.hint}
                          </div>
                        </div>
                        {isTypes && options.length > 0 ? (
                          <div className="flex flex-col gap-2">
                            {options.map((t) => {
                              const included = typeSet.size === 0 || typeSet.has(t.key);
                              return (
                                <div key={t.key} className="flex items-center gap-2.5">
                                  <input
                                    type="checkbox"
                                    checked={included}
                                    onChange={() => toggleActivityType(t.key)}
                                    className="h-[15px] w-[15px] shrink-0 cursor-pointer accent-primary"
                                  />
                                  <div className="w-40 shrink-0 truncate text-[13px] text-secondary" title={`${t.name} · ${t.key}`}>
                                    {t.name} <span className="font-mono text-[11px] text-muted">{t.key}</span>
                                  </div>
                                  <span className="text-[13px] text-muted">→</span>
                                  <input
                                    value={activityLabel(t.key)}
                                    placeholder={t.name}
                                    onChange={(e) => setActivityLabel(t.key, e.target.value)}
                                    className="min-w-0 flex-1 rounded-md border border-mist bg-paper px-2.5 py-1.5 text-[13px] text-dark outline-none focus:border-primary"
                                  />
                                </div>
                              );
                            })}
                            <span className="text-[12px] text-muted">
                              {typeSet.size === 0 ? "All types included." : `${typeSet.size} selected.`} Blank name = keep the original.
                            </span>
                          </div>
                        ) : (
                          <input
                            value={settings[f.key]}
                            onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                            className="w-full rounded-md border border-mist bg-paper px-3 py-2 font-mono text-[13px] text-dark outline-none focus:border-primary"
                          />
                        )}
                      </div>
                      {i < SETTING_FIELDS.length - 1 && <div className="mt-5 h-px bg-mist" />}
                    </div>
                  );
                })}
                <div className="flex items-center gap-3 pt-1">
                  <button
                    onClick={saveExportOptions}
                    disabled={busy}
                    className="rounded-md bg-primary px-[18px] py-2 text-[13px] font-semibold text-white hover:bg-primary-dark disabled:opacity-50"
                  >
                    Save options
                  </button>
                  {toast && <span className="text-[12.5px] text-muted">{toast}</span>}
                </div>
              </div>
            </>
          )}

          {view === "connection" && settings && (
            <>
              <div>
                <H1>Connection</H1>
                <Lead>One token reads Pipedrive, one lets Phocas pull the CSVs. Blank = use the deploy-time secret.</Lead>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {([
                  { key: "pipedriveApiToken" as const, rk: "pd" as const, title: "Pipedrive API token", note: "Reads your Pipedrive data. Blank = use the PIPEDRIVE_API_TOKEN secret." },
                  { key: "exportToken" as const, rk: "ex" as const, title: "Export token", note: "Bearer the Phocas server sends to pull the CSVs. Blank = use the EXPORT_TOKEN secret." },
                ]).map((c) => (
                  <div key={c.key} className="flex flex-col gap-3.5 rounded-lg border border-mist bg-paper p-5">
                    <div className="text-[14.5px] font-semibold text-headline">{c.title}</div>
                    <div className="flex gap-2">
                      <input
                        type={reveal[c.rk] ? "text" : "password"}
                        value={settings[c.key]}
                        onChange={(e) => setSettings({ ...settings, [c.key]: e.target.value })}
                        className="w-full rounded-md border border-mist bg-paper px-3 py-2 font-mono text-[13px] text-dark outline-none focus:border-primary"
                      />
                      <button
                        type="button"
                        onClick={() => setReveal({ ...reveal, [c.rk]: !reveal[c.rk] })}
                        className="rounded-md border border-mist px-2.5 text-xs text-muted hover:bg-mist"
                      >
                        {reveal[c.rk] ? "Hide" : "Show"}
                      </button>
                    </div>
                    <div className="text-[12.5px] leading-normal text-muted">{c.note}</div>
                    {c.rk === "pd" && (
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={testPipedrive}
                          disabled={pdTest === "testing"}
                          className="self-start rounded-md border border-mist px-3.5 py-1.5 text-[12.5px] font-semibold text-secondary hover:border-secondary disabled:opacity-50"
                        >
                          {pdTest === "testing" ? "Testing…" : "Test connection"}
                        </button>
                        {pdTest && pdTest !== "testing" && (
                          <div
                            className={`rounded-md border px-3 py-2 text-[12.5px] leading-normal ${
                              pdTest.ok ? "border-success/40 text-dark" : "border-danger/40 text-danger"
                            }`}
                          >
                            {pdTest.ok ? (
                              <>
                                <span className="font-semibold">Connected</span> as {pdTest.user || "unknown user"}
                                {pdTest.email && ` (${pdTest.email})`}
                                {pdTest.company && ` · ${pdTest.company}`}
                                {pdTest.domain && ` · ${pdTest.domain}.pipedrive.com`}
                              </>
                            ) : (
                              <>
                                <span className="font-semibold">Failed:</span> {pdTest.error}
                              </>
                            )}
                            {pdTest.source && (
                              <div className="mt-0.5 text-muted">
                                Tested the{" "}
                                {pdTest.source === "entered"
                                  ? "token in the field above"
                                  : pdTest.source === "saved"
                                    ? "saved token"
                                    : "PIPEDRIVE_API_TOKEN secret"}
                                .
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={saveSettings}
                  disabled={busy}
                  className="rounded-md bg-primary px-[18px] py-2 text-[13px] font-semibold text-white hover:bg-primary-dark disabled:opacity-50"
                >
                  Save connection
                </button>
                {toast && <span className="text-[12.5px] text-muted">{toast}</span>}
              </div>
            </>
          )}

          {view === "urls" && (
            <>
              <div>
                <H1>Pull URLs</H1>
                <Lead>Point the Phocas data sources at these. Add your export token as a Bearer header (or ?token=).</Lead>
              </div>
              <div className="overflow-hidden rounded-lg border border-mist bg-paper">
                {[
                  { name: "Deals", slug: "deals" },
                  { name: "Activities", slug: "activities" },
                ].map((u) => {
                  const url = `${origin}/export/${u.slug}.csv`;
                  return (
                    <div key={u.slug} className="flex items-center gap-4 border-b border-mist/70 px-[18px] py-3.5 last:border-b-0">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13.5px] font-semibold text-headline">{u.name}</div>
                        <div className="mt-1 truncate font-mono text-[12.5px] text-muted">{url}</div>
                      </div>
                      <button
                        onClick={() => copyUrl(u.slug, url)}
                        className="flex-none rounded-md border border-mist px-3.5 py-1.5 text-[12.5px] font-semibold text-secondary hover:border-secondary"
                      >
                        {copied === u.slug ? "Copied" : "Copy"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </main>
      </div>

      <footer className="bg-secondary">
        <div className="mx-auto flex max-w-[1180px] items-center justify-between px-7 py-4 text-xs text-white/55">
          <span>Spillcraft · Spill Control Solutions</span>
          <span>{window.location.host}</span>
        </div>
      </footer>
      <div className="h-[3px] w-full" style={GRADIENT} />
    </div>
  );
}
